import { PageTitle } from 'components/PageTitle'
import { FullPageSpinner } from 'components/Spinner'
import { BreakoutButton } from 'components/design-system/BreakoutButton'
import { signInWithCustomToken } from 'firebase/auth'
import { httpsCallable } from 'firebase/functions'
import { useFunctions, useRepository } from 'hooks/auth'
import { useRootStore } from 'hooks/rootStore'
import { useTranslationTyped } from 'i18n/i18n'
import { useEffect, useRef, useState } from 'react'
import { captureException } from '@sentry/react'

type MagicLinkResponse =
  | { status: 'success'; customToken: string; redirectTo?: string }
  | { status: 'already_logged_in'; redirectTo?: string }
  | { status: 'expired_new_sent' }
  | { status: 'expired_cooldown' }
  | { status: 'invalid' }

type PageState =
  | 'loading'
  | 'signing_in'
  | 'expired_new_sent'
  | 'expired_cooldown'
  | 'invalid'

export function MagicLinkPage() {
  const rootStore = useRootStore()
  const repository = useRepository()
  const functions = useFunctions()
  const { tt } = useTranslationTyped()
  const [pageState, setPageState] = useState<PageState>('loading')
  const hasProcessedTokenRef = useRef(false)

  const queryParams = new URLSearchParams(window.location.search)
  const token = queryParams.get('token')

  useEffect(() => {
    if (hasProcessedTokenRef.current) return
    hasProcessedTokenRef.current = true

    if (!token) {
      setPageState('invalid')
      return
    }

    const handleMagicToken = httpsCallable<
      { token: string },
      MagicLinkResponse
    >(functions, 'handleMagicToken')

    handleMagicToken({ token })
      .then(async (result) => {
        const response = result.data

        switch (response.status) {
          case 'success':
            setPageState('signing_in')
            await signInWithCustomToken(repository.auth, response.customToken)
            await repository.waitForBreakoutUser()
            if (response.redirectTo) {
              rootStore.navigateToUrl(response.redirectTo)
            } else {
              rootStore.navigateTo('home')
            }
            break

          case 'already_logged_in':
            if (response.redirectTo) {
              rootStore.navigateToUrl(response.redirectTo)
            } else {
              rootStore.navigateTo('home')
            }
            break

          case 'expired_new_sent':
            setPageState('expired_new_sent')
            break

          case 'expired_cooldown':
            setPageState('expired_cooldown')
            break

          case 'invalid':
            setPageState('invalid')
            break
        }
      })
      .catch((error) => {
        captureException(error)
        setPageState('invalid')
      })
  }, [token, functions, repository, rootStore])

  if (pageState === 'loading' || pageState === 'signing_in') {
    return <FullPageSpinner />
  }

  if (pageState === 'expired_new_sent') {
    return (
      <MagicLinkMessage
        title={tt.magic_link.expired_title()}
        message={tt.magic_link.expired_message()}
        showHomeButton
      />
    )
  }

  if (pageState === 'expired_cooldown') {
    return (
      <MagicLinkMessage
        title={tt.magic_link.cooldown_title()}
        message={tt.magic_link.cooldown_message()}
        showHomeButton
      />
    )
  }

  // invalid state
  return (
    <MagicLinkMessage
      title={tt.magic_link.invalid_title()}
      message={tt.magic_link.invalid_message()}
      showHomeButton
    />
  )
}

function MagicLinkMessage({
  title,
  message,
  showHomeButton,
}: {
  title: string
  message: string
  showHomeButton?: boolean
}) {
  const rootStore = useRootStore()
  const { tt } = useTranslationTyped()

  return (
    <div className="flex h-full w-full flex-col items-center justify-center bg-core-tertiary">
      <PageTitle title={tt.magic_link.page_title()} />
      <div className="max-w-[400px] text-center">
        <img
          src="/assets/images/logo.png"
          width={64}
          className="mx-auto mb-8"
          alt={tt.breakout_learning()}
        />
        <h1 className="mb-4 text-headline-large">{title}</h1>
        <p className="mb-8 text-body-large">{message}</p>
        {showHomeButton && (
          <BreakoutButton
            kind="accent"
            onClick={() => rootStore.navigateTo('home')}
          >
            {tt.magic_link.go_home()}
          </BreakoutButton>
        )}
      </div>
    </div>
  )
}
